import { createSubstore } from 'storeon-substore';
import type { Store } from 'store/types';
import { getCollectionInfo, getCollectionList } from './collections-actions';
import { FETCH_COLLECTIONS, FETCH_COLLECTION_INFO, COLLECTIONS_STORE_NAME } from './collections-constants';
import { Collection, CollectionFacet, CollectionsStore, KitchenCollection } from './collections-types';

const INITIAL_STATE: CollectionsStore = {
  collectionList: null,
  facets: [] as CollectionFacet[],
  offset: 0,
  collectionInfo: null,
  productsCount: 0,
  loading: false,
  error: null,
} as const;

export const collections = (parentStore: Store) => {
  const store = createSubstore(parentStore, COLLECTIONS_STORE_NAME);
  store.on('@init', () => INITIAL_STATE);
  store.on(FETCH_COLLECTIONS.init, async (_, { url, ...rest }) => {
    const { loading } = store.get();
    const { modelJson } = parentStore.get();
    if (!modelJson.model || loading) {
      return Promise.resolve();
    }
    store.dispatch(FETCH_COLLECTIONS.request);

    try {
      const collectionList = await getCollectionList(parentStore, url, {
        regionId: modelJson.model.properties.regionId,
        ...rest,
      });
      store.dispatch(FETCH_COLLECTIONS.success, collectionList);
    } catch (e) {
      store.dispatch(FETCH_COLLECTIONS.failure, (e as Error).message);
    }

    return Promise.resolve();
  });
  store.on(FETCH_COLLECTIONS.request, state => ({ ...state, loading: true }));
  store.on(FETCH_COLLECTIONS.clear, state => ({
    ...state,
    collectionList: [],
    offset: 0,
    productsCount: 0,
  }));
  store.on(FETCH_COLLECTIONS.failure, (state, error) => ({
    ...state,
    error,
    loading: false,
    collectionList: null,
  }));
  store.on(FETCH_COLLECTIONS.success, (state, res) => {
    if ('collectionList' in res) {
      return {
        ...state,
        offset: res?.offset,
        facets: res?.facets,
        productsCount: res?.productsCount,
        collectionList: [
          ...(state.collectionList || []),
          ...(res?.collectionList || []),
        ] as KitchenCollection[],
        loading: false,
      };
    }

    return {
      ...state,
      collectionList: res as Collection[],
      loading: false,
    };
  });

  store.on(FETCH_COLLECTION_INFO.init, async (_, { url, ...rest }) => {
    const { loading } = store.get();
    const { modelJson } = parentStore.get();
    if (!modelJson.model || loading) {
      return Promise.resolve();
    }
    store.dispatch(FETCH_COLLECTION_INFO.request);

    try {
      const collectionInfo = await getCollectionInfo(parentStore, url, {
        regionId: modelJson.model.properties.regionId,
        ...rest,
      });
      store.dispatch(FETCH_COLLECTION_INFO.success, collectionInfo);
    } catch (e) {
      store.dispatch(FETCH_COLLECTION_INFO.failure, (e as Error).message);
    }

    return Promise.resolve();
  });
  store.on(FETCH_COLLECTION_INFO.request, state => ({ ...state, loading: true }));
  store.on(FETCH_COLLECTION_INFO.failure, (state, error) => ({
    ...state,
    error,
    loading: false,
    collectionInfo: null,
  }));
  store.on(FETCH_COLLECTION_INFO.success, (state, res) => ({
    ...state,
    collectionInfo: res,
    loading: false,
  }));
};
